import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import { useHubBasePath } from "../../../utils/hubPaths";
import ClientPortalAccessSection from "./ClientPortalAccessSection";
import ClientMedicalSection from "./ClientMedicalSection";
import ClientNotesSection from "./ClientNotesSection";
import ClientGuardianStrip from "./ClientGuardianStrip";
import ClientChildrenStrip from "./ClientChildrenStrip";
import { ClientInfoSection } from "./ClientProfileSections";
import {
  ClientLevelSection,
  ClientRankSection,
} from "./ClientQualificationSection";
import {
  levelPreview,
  medicalClearancePreview,
  rankPreview,
  formatShortDate,
} from "./athleteQualifications";
import { portalAccessPreview } from "./patientConstants";
import { COMPETITION_STATUS_LABELS } from "./clientSportDisplay";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import { Button, EmptyState } from "../../ui";

const COMP_STATUS = COMPETITION_STATUS_LABELS;

function OverviewFold({ id, title, preview, open, onToggle, children, action, icon, tone = "default" }) {
  return (
    <section
      className={`${aa.clientSection} ${open ? aa.clientSectionOpen : ""} ${aa[`clientSection_${tone}`] || ""}`}
    >
      <button
        type="button"
        className={aa.clientSectionToggle}
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
      >
        {icon ? (
          <span className={`${aa.clientSectionIcon} ${aa[`clientSectionIcon_${tone}`] || ""}`}>
            {icon}
          </span>
        ) : null}
        <span className={aa.clientSectionToggleMain}>
          <h3 className={aa.clientSectionTitle}>{title}</h3>
          {preview ? (
            <span className={aa.clientSectionPreview}>{preview}</span>
          ) : null}
        </span>
        {action ? (
          <span
            className={aa.clientSectionInlineAction}
            onClick={(e) => {
              e.stopPropagation();
              action.onClick();
            }}
            role="presentation"
          >
            {action.label}
          </span>
        ) : null}
        <span className={aa.clientSectionChevron} aria-hidden />
      </button>
      {open ? (
        <div id={id} className={aa.clientSectionBody}>
          {children}
        </div>
      ) : null}
    </section>
  );
}

function ClientOverviewApp({
  patientId,
  patient,
  profile,
  guardians = [],
  edit,
  setEdit,
  onSave,
  subdomain,
  newPassword,
  setNewPassword,
  onSavePortalEmail,
  onSetPortalPassword,
  savingPortalEmail,
  settingPortalPassword,
  editingInfo,
  onToggleEdit,
  onAddToCompetition,
  inFold = false,
  overviewFocus = null,
}) {
  const hubBase = useHubBasePath();
  const [openId, setOpenId] = useState("groups");
  const [editingMed, setEditingMed] = useState(false);
  const [editingLevel, setEditingLevel] = useState(false);
  const [editingRank, setEditingRank] = useState(false);

  useEffect(() => {
    if (overviewFocus?.id) setOpenId(overviewFocus.id);
  }, [overviewFocus]);
  const groups = profile?.groups || [];
  const individual = profile?.individual_sessions || [];
  const competitions = profile?.competitions || [];
  const sport = profile?.client?.sport;
  const isGuardianAccount =
    sport?.account_kind === "guardian" || sport?.also_guardian;
  const childAccount = Boolean(
    sport?.primary_guardian || guardians.length > 0,
  );

  const portalPreview = portalAccessPreview(
    {
      email: edit.email,
      portal_login_email: edit.email,
      portal_activated: patient?.is_activated,
    },
    { childAccount },
  );
  const portalTitle = childAccount ? "Кабинет спортсмена" : "Личный кабинет";

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  const groupsPreview =
    groups.length > 0
      ? groups
          .slice(0, 2)
          .map((g) => g.name)
          .join(", ")
      : isGuardianAccount
        ? "не занимается"
        : "не в группах";

  return (
    <div className={aa.clientSections}>
      <ClientChildrenStrip profile={profile} sport={sport} />
      <ClientGuardianStrip
        guardians={guardians}
        primaryGuardian={sport?.primary_guardian}
      />
      <div className={aa.clientSectionGroup}>
        <OverviewFold
          id="client-groups"
          title="Группы"
          preview={groupsPreview}
          open={openId === "groups"}
          onToggle={() => toggle("groups")}
          icon="🏃"
          tone="blue"
        >
          {isGuardianAccount ? (
            groups.length > 0 ? (
              <ul className={aa.clientOverviewList}>
                {groups.map((g) => (
                  <li key={g.id}>
                    <Link
                      to={`${hubBase}/groups?group=${g.id}`}
                      className={aa.clientOverviewLink}
                    >
                      <strong>{g.name}</strong>
                      <span>
                        {[g.sport_direction, g.trainer_name]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                title="Родитель не в группах"
                description="Если сам занимается — зачислите в группу. Группы детей смотрите в блоке «Дети» выше."
              />
            )
          ) : groups.length === 0 ? (
            <EmptyState
              title="Не в группах"
              description="Зачислите в разделе «Группы»"
            />
          ) : (
            <ul className={aa.clientOverviewList}>
              {groups.map((g) => (
                <li key={g.id}>
                  <Link
                    to={`${hubBase}/groups?group=${g.id}`}
                    className={aa.clientOverviewLink}
                  >
                    <strong>{g.name}</strong>
                    <span>
                      {[g.sport_direction, g.trainer_name].filter(Boolean).join(" · ")}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <Link to={`${hubBase}/groups`} className={aa.clientOverviewFootLink}>
            {isGuardianAccount && !groups.length
              ? "Зачислить родителя в группу"
              : "Открыть группы"}
          </Link>
        </OverviewFold>

        <OverviewFold
          id="client-individual"
          title="Индивидуальные"
          preview={
            individual.length
              ? `${individual.length} занятий`
              : "нет запланированных"
          }
          open={openId === "individual"}
          onToggle={() => toggle("individual")}
          icon="🎯"
          tone="purple"
        >
          {individual.length === 0 ? (
            <p className={aa.clientOverviewMuted}>
              Создайте в календаре — формат «Индивидуальная».
            </p>
          ) : (
            <ul className={aa.clientOverviewList}>
              {individual.slice(0, 5).map((sess) => (
                <li key={sess.id}>
                  <Link
                    to={`${hubBase}/schedule?session=${sess.id}`}
                    className={aa.clientOverviewLink}
                  >
                    <strong>
                      {formatDateRu(sess.start_datetime, {
                        day: "numeric",
                        month: "short",
                      })}{" "}
                      {formatTimeRu(sess.start_datetime)}
                    </strong>
                    <span>
                      {[sess.trainer_name, sess.branch_name].filter(Boolean).join(" · ")}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <Link
            to={`${hubBase}/schedule?create=individual&patient=${patientId}`}
            className={aa.clientOverviewFootLink}
          >
            Запланировать
          </Link>
        </OverviewFold>

        <OverviewFold
          id="client-competitions"
          title="Соревнования"
          preview={
            competitions.length
              ? `${competitions.length} заявок`
              : "без заявок"
          }
          open={openId === "competitions"}
          onToggle={() => toggle("competitions")}
          icon="🏆"
          tone="gold"
        >
          {competitions.length === 0 ? (
            <p className={aa.clientOverviewMuted}>
              Заявка из личного кабинета или списка участников события.
            </p>
          ) : (
            <ul className={aa.clientOverviewList}>
              {competitions.map((c) => (
                <li key={c.id} className={aa.clientOverviewCompRow}>
                  <div>
                    <strong>{c.session?.title || "Соревнование"}</strong>
                    {c.session?.start_datetime ? (
                      <span className={aa.clientOverviewMuted}>
                        {formatDateRu(c.session.start_datetime, {
                          day: "numeric",
                          month: "long",
                        })}
                      </span>
                    ) : null}
                  </div>
                  <span className={aa.clientOverviewStatus}>
                    {COMP_STATUS[c.status] || c.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {onAddToCompetition ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={onAddToCompetition}
            >
              Добавить на соревнование
            </Button>
          ) : null}
        </OverviewFold>

        <OverviewFold
          id="client-level"
          title="Уровень"
          preview={levelPreview(edit)}
          open={openId === "level"}
          onToggle={() => toggle("level")}
          icon="🥋"
          tone="gold"
          action={
            editingLevel
              ? undefined
              : {
                  label: "Изменить",
                  onClick: () => {
                    setOpenId("level");
                    setEditingLevel(true);
                  },
                }
          }
        >
          <ClientLevelSection
            edit={edit}
            setEdit={setEdit}
            onSave={onSave}
            editing={editingLevel}
            onToggleEdit={() => setEditingLevel((value) => !value)}
          />
        </OverviewFold>

        <OverviewFold
          id="client-rank"
          title="Разряд"
          preview={rankPreview(edit)}
          open={openId === "rank"}
          onToggle={() => toggle("rank")}
          icon="🎖️"
          tone="purple"
          action={
            editingRank
              ? undefined
              : {
                  label: "Изменить",
                  onClick: () => {
                    setOpenId("rank");
                    setEditingRank(true);
                  },
                }
          }
        >
          <ClientRankSection
            edit={edit}
            setEdit={setEdit}
            onSave={onSave}
            editing={editingRank}
            onToggleEdit={() => setEditingRank((value) => !value)}
          />
        </OverviewFold>

        <OverviewFold
          id="client-info"
          title="Контакты"
          preview={
            [
              patient?.date_of_birth
                ? formatShortDate(patient.date_of_birth)
                : "нет даты рождения",
              patient?.phone,
            ]
              .filter(Boolean)
              .join(" · ")
          }
          open={openId === "info"}
          onToggle={() => toggle("info")}
          icon="📇"
          tone="teal"
          action={
            editingInfo
              ? undefined
              : {
                  label: "Изменить",
                  onClick: () => {
                    if (inFold) setOpenId("info");
                    onToggleEdit();
                  },
                }
          }
        >
          <ClientInfoSection
            patient={patient}
            edit={edit}
            setEdit={setEdit}
            onSave={onSave}
            editing={editingInfo}
            onToggleEdit={onToggleEdit}
            embedded
            inFold={inFold}
          />
        </OverviewFold>

        <OverviewFold
          id="client-medical"
          title="Мед.допуск"
          preview={medicalClearancePreview(edit)}
          open={openId === "medical"}
          onToggle={() => toggle("medical")}
          icon="🩺"
          tone="green"
          action={
            inFold && !editingMed
              ? {
                  label: "Обновить",
                  onClick: () => {
                    setOpenId("medical");
                    setEditingMed(true);
                  },
                }
              : undefined
          }
        >
          <ClientMedicalSection
            edit={edit}
            setEdit={setEdit}
            onSave={onSave}
            editing={editingMed}
            onToggleEdit={() => setEditingMed((v) => !v)}
            embedded
            inFold={inFold}
          />
        </OverviewFold>

        <OverviewFold
          id="client-portal"
          title={portalTitle}
          preview={portalPreview}
          open={openId === "portal"}
          onToggle={() => toggle("portal")}
          icon="🔐"
          tone="indigo"
        >
          <ClientPortalAccessSection
            patientId={patientId}
            patient={patient}
            edit={edit}
            setEdit={setEdit}
            subdomain={subdomain}
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            onSaveEmail={onSavePortalEmail}
            onSetPassword={onSetPortalPassword}
            savingEmail={savingPortalEmail}
            settingPassword={settingPortalPassword}
            inFold={inFold}
            childAccount={childAccount}
          />
        </OverviewFold>

        <OverviewFold
          id="client-notes"
          title="Заметки"
          preview="история тренера"
          open={openId === "notes"}
          onToggle={() => toggle("notes")}
          icon="📝"
          tone="slate"
        >
          <ClientNotesSection
            patientId={patientId}
            legacyNote={patient?.internal_notes}
          />
        </OverviewFold>
      </div>
    </div>
  );
}

export default function ClientOverviewSection({
  patientId,
  patient,
  profile,
  guardians = [],
  edit,
  setEdit,
  onSave,
  subdomain,
  newPassword,
  setNewPassword,
  onSavePortalEmail,
  onSetPortalPassword,
  savingPortalEmail,
  settingPortalPassword,
  editingInfo,
  onToggleEdit,
  onAddToCompetition,
  compact = false,
  social = false,
  overviewFocus = null,
}) {
  const hubBase = useHubBasePath();

  if (compact || social) {
    return (
      <ClientOverviewApp
        patientId={patientId}
        patient={patient}
        profile={profile}
        guardians={guardians}
        edit={edit}
        setEdit={setEdit}
        onSave={onSave}
        subdomain={subdomain}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onSavePortalEmail={onSavePortalEmail}
        onSetPortalPassword={onSetPortalPassword}
        savingPortalEmail={savingPortalEmail}
        settingPortalPassword={settingPortalPassword}
        editingInfo={editingInfo}
        onToggleEdit={onToggleEdit}
        onAddToCompetition={onAddToCompetition}
        inFold={social}
        overviewFocus={overviewFocus}
      />
    );
  }

  const groups = profile?.groups || [];
  const individual = profile?.individual_sessions || [];
  const competitions = profile?.competitions || [];
  const sport = profile?.client?.sport;
  const isGuardianAccount =
    sport?.account_kind === "guardian" || sport?.also_guardian;
  const childAccount = Boolean(
    sport?.primary_guardian || guardians.length > 0,
  );

  return (
    <div className={s.clientProfileTabPanel}>
      <div className={s.clientTabCard}>
        <div className={s.clientProfileEmbeddedHead}>
          <h3 className={s.clientTabCardTitle}>Группы</h3>
          <Link to={`${hubBase}/groups`} className={s.clientProfileInlineLink}>
            Все группы
          </Link>
        </div>
        {isGuardianAccount ? (
          groups.length > 0 ? (
            <ul className={s.clientGroupList}>
              {groups.map((g) => (
                <li key={g.id} className={s.clientGroupListItem}>
                  <Link
                    to={`${hubBase}/groups?group=${g.id}`}
                    className={s.clientGroupLink}
                  >
                    <strong>{g.name}</strong>
                  </Link>
                  {g.sport_direction ? (
                    <span className={s.clientTabMuted}>{g.sport_direction}</span>
                  ) : null}
                  {g.trainer_name ? (
                    <span className={s.clientTabMuted}>
                      Тренер: {g.trainer_name}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="Родитель не в группах"
              description="Если сам занимается — зачислите в группу. Группы детей — в блоке «Дети»."
            />
          )
        ) : groups.length === 0 ? (
          <EmptyState
            title="Не в группах"
            description="Зачислите клиента в группу в разделе «Группы»"
          />
        ) : (
          <ul className={s.clientGroupList}>
            {groups.map((g) => (
              <li key={g.id} className={s.clientGroupListItem}>
                <Link
                  to={`${hubBase}/groups?group=${g.id}`}
                  className={s.clientGroupLink}
                >
                  <strong>{g.name}</strong>
                </Link>
                {g.sport_direction ? (
                  <span className={s.clientTabMuted}>{g.sport_direction}</span>
                ) : null}
                {g.trainer_name ? (
                  <span className={s.clientTabMuted}>Тренер: {g.trainer_name}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={s.clientTabCard}>
        <div className={s.clientProfileEmbeddedHead}>
          <h3 className={s.clientTabCardTitle}>Индивидуальные тренировки</h3>
          <Link
            to={`${hubBase}/schedule?create=individual&patient=${patientId}`}
            className={s.clientProfileInlineLink}
          >
            Запланировать
          </Link>
        </div>
        {individual.length === 0 ? (
          <p className={s.clientTabMuted}>
            Нет запланированных индивидуальных занятий. Создайте в календаре — формат «Индивидуальная».
          </p>
        ) : (
          <ul className={s.clientOverviewSessionList}>
            {individual.map((sess) => (
              <li key={sess.id} className={s.clientOverviewSessionItem}>
                <Link
                  to={`${hubBase}/schedule?session=${sess.id}`}
                  className={s.clientGroupLink}
                >
                  <strong>
                    {formatDateRu(sess.start_datetime, {
                      day: "numeric",
                      month: "short",
                    })}{" "}
                    {formatTimeRu(sess.start_datetime)}
                  </strong>
                </Link>
                {sess.trainer_name ? (
                  <span className={s.clientTabMuted}>{sess.trainer_name}</span>
                ) : null}
                {sess.branch_name ? (
                  <span className={s.clientTabMuted}>{sess.branch_name}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={s.clientTabCard}>
        <div className={s.clientProfileEmbeddedHead}>
          <h3 className={s.clientTabCardTitle}>Соревнования</h3>
          <Link
            to={`${hubBase}/schedule?create=event`}
            className={s.clientProfileInlineLink}
          >
            Создать событие
          </Link>
        </div>
        {competitions.length === 0 ? (
          <p className={s.clientTabMuted}>
            Заявок пока нет. Клиент может подать заявку в личном кабинете, тренер — добавить в список участников события.
          </p>
        ) : (
          <ul className={s.clientOverviewSessionList}>
            {competitions.map((c) => (
              <li key={c.id} className={s.clientOverviewSessionItem}>
                <strong>{c.session?.title || "Соревнование"}</strong>
                {c.session?.start_datetime ? (
                  <span className={s.clientTabMuted}>
                    {formatDateRu(c.session.start_datetime, {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                ) : null}
                <span className={s.clientCompetitionStatus}>
                  {COMP_STATUS[c.status] || c.status}
                </span>
                {c.session?.id ? (
                  <Link
                    to={`${hubBase}/schedule?session=${c.session.id}`}
                    className={s.clientProfileInlineLink}
                  >
                    Список участников
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {onAddToCompetition ? (
          <Button type="button" size="sm" variant="secondary" onClick={onAddToCompetition}>
            Добавить на соревнование
          </Button>
        ) : null}
      </div>

      <ClientInfoSection
        patient={patient}
        edit={edit}
        setEdit={setEdit}
        onSave={onSave}
        editing={editingInfo}
        onToggleEdit={onToggleEdit}
        embedded
      />

      <ClientPortalAccessSection
        patientId={patientId}
        patient={patient}
        edit={edit}
        setEdit={setEdit}
        subdomain={subdomain}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onSaveEmail={onSavePortalEmail}
        onSetPassword={onSetPortalPassword}
        savingEmail={savingPortalEmail}
        settingPassword={settingPortalPassword}
        childAccount={childAccount}
      />
    </div>
  );
}
